import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { useHistory, useParams } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import { IoPrintOutline } from "react-icons/io5";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import LoginUserDetailed from "../../Auth/LoginUserDetailed";
import { errorAlert, handleSuccess } from "../../Components/Alerts";
import { getUnitID } from "../../Auth/GetUnitCompany";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import OwnersModal from "../OwnersCRUD/OwnersModal";
import DateInputDdMmYyyy from "../../Components/DateInputDdMmYyyy";
import PriceField from "../../Components/NumberFieldFormater";
import "../ReceivablesCRUD/ReceivablesForm.scss";
import "./CapitalDepositCRUD.scss";

const getDefaultDateForFiscalYear = (fiscalYear) => {
  const todayIso = new Date().toISOString().slice(0, 10);
  const minFiscalDate = `${fiscalYear}-01-01`;
  const maxFiscalDate = `${fiscalYear}-12-31`;

  if (todayIso < minFiscalDate) return minFiscalDate;
  if (todayIso > maxFiscalDate) return maxFiscalDate;
  return todayIso;
};

const createEmptySnapshot = (defaultDateForFiscalYear) => ({
  date: defaultDateForFiscalYear || "",
  invoiceNR: "",
  ownerID: "",
  ledgerID: "",
  paymentOptions: "",
  total: "",
  coment: "",
});

function CapitalDepositForm() {
  const history = useHistory();
  const { transactionId } = useParams();
  const isEditMode = Boolean(transactionId);

  const headers = useMemo(() => authHeader(), []);
  const email = LoginUserDetailed();
  const { fiscalYear } = useContext(FiscalYearContext);

  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [invoiceNR, setInvoiceNR] = useState("");
  const [ownerID, setOwnerID] = useState("");
  const [ledgerID, setLedgerID] = useState("");
  const [total, setTotal] = useState("");
  const [coment, setComent] = useState("");
  const [paymentOptions, setPaymentOptions] = useState("");
  const [unId, setUnID] = useState("");

  const [accounts, setAccounts] = useState([]);
  const [owners, setOwners] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [ownerField, setOwnerField] = useState(null);
  const [payField, setPayField] = useState(null);

  const [saving, setSaving] = useState(false);
  const [isInitializing, setIsInitializing] = useState(isEditMode);
  const [savedDeposit, setSavedDeposit] = useState(null);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [validationErrors, setValidationErrors] = useState({
    date: "",
    ownerID: "",
    ledgerID: "",
    paymentOptions: "",
    total: "",
    coment: "",
  });
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");

  const defaultDateForFiscalYear = getDefaultDateForFiscalYear(fiscalYear);
  const [initialFormSnapshot, setInitialFormSnapshot] = useState(
    createEmptySnapshot(defaultDateForFiscalYear),
  );

  const currentFormSnapshot = {
    date: date || "",
    invoiceNR: String(invoiceNR || ""),
    ownerID: String(ownerID || ""),
    ledgerID: String(ledgerID || ""),
    paymentOptions: String(paymentOptions || ""),
    total: String(total ?? ""),
    coment: String(coment || ""),
  };

  const isFormDirty =
    JSON.stringify(currentFormSnapshot) !== JSON.stringify(initialFormSnapshot);

  const hasUnsavedData = isEditMode
    ? isFormDirty
    : Boolean(
        ledgerID ||
          paymentOptions ||
          total ||
          ownerID ||
          coment.trim() ||
          (date && date !== defaultDateForFiscalYear),
      );

  useEffect(() => {
    setMinDate(`${fiscalYear}-01-01`);
    setMaxDate(`${fiscalYear}-12-31`);

    setDate((previousDate) => {
      if (!previousDate) return `${fiscalYear}-01-01`;
      if (previousDate < `${fiscalYear}-01-01`) return `${fiscalYear}-01-01`;
      if (previousDate > `${fiscalYear}-12-31`) return `${fiscalYear}-12-31`;
      return previousDate;
    });
  }, [fiscalYear]);

  const markDirty = () => {
    setSavedDeposit(null);
  };

  const clearValidationError = (name) => {
    setValidationErrors((prev) => {
      if (!prev[name]) return prev;
      return { ...prev, [name]: "" };
    });
  };

  const fetchAccounts = async (ledgerValue) => {
    if (!ledgerValue) {
      setAccounts([]);
      return;
    }

    try {
      const response = await axios.get(ApiLink.API_URL + "Accounts/accountByLedger", {
        headers,
        params: { ledger: ledgerValue },
      });
      setAccounts(response.data || []);
    } catch {
      errorAlert("Gabim në marrjen e llogarive.");
    }
  };

  const fetchLedger = async () => {
    try {
      const response = await axios.get(ApiLink.API_URL + "Ledger/Get_101_102_Ledgers", { headers });
      setLedgers(response.data || []);
    } catch {
      errorAlert("Gabim në marrjen e ledgerëve.");
    }
  };

  const fetchOwner = async () => {
    try {
      const response = await axios.get(ApiLink.API_URL + "Accounts/accountByLedger", {
        headers,
        params: { ledger: 501 },
      });
      setOwners(response.data || []);
    } catch {
      errorAlert("Gabim në marrjen e pronarëve.");
    }
  };

  const fetchNextInvoiceNumber = async () => {
    try {
      const response = await axios.get(ApiLink.API_URL + "CapitalDeposite/InvoiceNr", {
        headers,
        params: { fiscalYear },
      });
      setInvoiceNR(response.data?.NextInvoiceNr || "");
    } catch {
      errorAlert("Gabim në marrjen e numrit të deponimit.");
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const response = await axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers });
      if (Array.isArray(response?.data) && response.data.length > 0) {
        setCompanyDetails(response.data[0]);
      }
    } catch {
      setCompanyDetails(null);
    }
  };

  useEffect(() => {
    getUnitID(headers).then(setUnID);
  }, [headers]);

  useEffect(() => {
    fetchOwner();
    fetchLedger();
    fetchCompanyDetails();

    if (!isEditMode) {
      fetchNextInvoiceNumber();
      setDate(defaultDateForFiscalYear);
      setInitialFormSnapshot(createEmptySnapshot(defaultDateForFiscalYear));
      setIsInitializing(false);
    }
  }, [fiscalYear, isEditMode]);

  useEffect(() => {
    const loadEditData = async () => {
      if (!isEditMode) return;

      setIsInitializing(true);
      try {
        const response = await axios.get(`${ApiLink.API_URL}CapitalDeposite/getAllCapitalDepositData`, {
          headers,
          params: {
            fiscalYear,
            page: 0,
            pageSize: 5000,
          },
        });

        const items = response?.data?.items || [];
        const existing = items.find((item) => String(item.TransactionID) === String(transactionId));

        if (!existing) {
          errorAlert("Deponimi nuk u gjet për editim.");
          history.push("/money/capitalDeposit");
          return;
        }

        const existingDate = existing.Date ? String(existing.Date).slice(0, 10) : defaultDateForFiscalYear;
        const existingInvoiceNr = String(existing.InvoiceNr || "");
        const existingOwnerID = String(existing.AccountID || existing.SupplierID || "");
        const existingLedgerID = String(existing.LedgerID || "");
        const existingPaymentOptions = String(existing.PaymentOptions || "");
        const existingTotal = String(existing.Total ?? "");
        const existingComent = String(existing.Coment || "");

        setDate(existingDate);
        setInvoiceNR(existingInvoiceNr);
        setOwnerID(existingOwnerID);
        setLedgerID(existingLedgerID);
        setPaymentOptions(existingPaymentOptions);
        setTotal(existingTotal);
        setComent(existingComent);

        if (existing.LedgerID) {
          await fetchAccounts(existing.LedgerID);
        }

        setInitialFormSnapshot({
          date: existingDate,
          invoiceNR: existingInvoiceNr,
          ownerID: existingOwnerID,
          ledgerID: existingLedgerID,
          paymentOptions: existingPaymentOptions,
          total: existingTotal,
          coment: existingComent,
        });
      } catch {
        errorAlert("Gabim në ngarkimin e deponimit për editim.");
      } finally {
        setIsInitializing(false);
      }
    };

    loadEditData();
  }, [isEditMode, transactionId, fiscalYear, headers, history, defaultDateForFiscalYear]);

  useEffect(() => {
    if (!owners.length || !ownerID) {
      if (!ownerID) setOwnerField(null);
      return;
    }

    const selected = owners.find((option) => option.AccountID === Number(ownerID));
    if (selected) {
      setOwnerField(selected);
    }
  }, [owners, ownerID]);

  useEffect(() => {
    if (!accounts.length || !paymentOptions) {
      if (!paymentOptions) setPayField(null);
      return;
    }

    const selected = accounts.find((option) => option.AccountID === Number(paymentOptions));
    if (selected) {
      setPayField(selected);
    }
  }, [accounts, paymentOptions]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedData) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedData]);

  const handleClose = () => {
    if (hasUnsavedData) {
      setShowConfirmClose(true);
      return;
    }

    history.push("/money/capitalDeposit");
  };

  const confirmClose = () => {
    setShowConfirmClose(false);
    history.push("/money/capitalDeposit");
  };

  const handleOwnerModalClose = () => {
    setShowOwnerModal(false);
    fetchOwner();
  };

  const handlePrint = () => {
    window.print();
  };

  const handleAutocompleteChangePayAccount = (event, value) => {
    if (value && value.AccountID) {
      setPaymentOptions(value.AccountID);
      setPayField(value);
      clearValidationError("paymentOptions");
      markDirty();
    } else {
      setPaymentOptions("");
      setPayField(null);
      markDirty();
    }
  };

  const defaultPropsPayAccount = {
    options: accounts,
    getOptionLabel: (option) => option.AccountName || "",
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSaving(true);

    const nextErrors = {
      date: "",
      ownerID: "",
      ledgerID: "",
      paymentOptions: "",
      total: "",
      coment: "",
    };

    if (!date) {
      nextErrors.date = "Data është e detyrueshme.";
    } else if (date < minDate || date > maxDate) {
      nextErrors.date = "Data duhet të jetë brenda vitit fiskal.";
    }

    if (!ownerID) {
      nextErrors.ownerID = "Ju lutem zgjidhni pronarin.";
    }

    if (!ledgerID) {
      nextErrors.ledgerID = "Ju lutem zgjidhni llogarinë kontabël.";
    }

    if (!paymentOptions) {
      nextErrors.paymentOptions = "Ju lutem zgjidhni llogarinë e deponimit.";
    }

    if (!total || Number(total) <= 0) {
      nextErrors.total = "Ju lutem plotësoni vlerë valide më të madhe se 0.";
    }

    if (!coment || !coment.trim()) {
      nextErrors.coment = "Ju lutem plotësoni komentin.";
    }

    if (Object.values(nextErrors).some(Boolean)) {
      setValidationErrors(nextErrors);
      setSaving(false);
      return;
    }

    setValidationErrors({
      date: "",
      ownerID: "",
      ledgerID: "",
      paymentOptions: "",
      total: "",
      coment: "",
    });

    const payload = {
      Date: date,
      ActionType: "CAD",
      InvoiceNr: invoiceNR,
      Total: total,
      SupplierID: ownerID,
      AccountID: ownerID,
      LedgerID: ledgerID,
      PaymentOptions: paymentOptions,
      CompanyUnitID: unId > 0 ? unId : null,
      Coment: coment,
      ...(isEditMode
        ? {
            TransactionID: Number(transactionId),
            ModifiedByUser: email,
          }
        : {
            CreatedByUser: email,
          }),
    };

    const request = isEditMode
      ? axios.put(ApiLink.API_URL + "CapitalDeposite/update-capital-deposit", payload, { headers })
      : axios.post(ApiLink.API_URL + "CapitalDeposite/insert-capital-deposit", payload, { headers });

    request
      .then(() => {
        const ownerAccount = owners.find((owner) => owner.AccountID === Number(ownerID));
        const selectedPaymentAccount = accounts.find((account) => account.AccountID === Number(paymentOptions));
        const selectedLedger = ledgers.find((item) => item.LedgerID === Number(ledgerID));

        setSavedDeposit({
          ...payload,
          DocumentNumber: `CAD-${fiscalYear}-${invoiceNR}`,
          OwnerAccountName: ownerAccount?.AccountName || "",
          OwnerIdentification: ownerAccount?.IdentificationNumber || "",
          PaymentAccountName: selectedPaymentAccount?.AccountName || "",
          LedgerName: selectedLedger?.LedgerNameEN || "",
        });

        handleSuccess(isEditMode ? "Deponimi u përditësua me sukses" : "Capital Deposit Added Successfully");

        if (isEditMode) {
          setSaving(false);
          setInitialFormSnapshot({
            date: date,
            invoiceNR: String(invoiceNR || ""),
            ownerID: String(ownerID || ""),
            ledgerID: String(ledgerID || ""),
            paymentOptions: String(paymentOptions || ""),
            total: String(total ?? ""),
            coment: String(coment || ""),
          });
          history.push("/money/capitalDeposit");
          return;
        }

        setDate(defaultDateForFiscalYear);
        fetchNextInvoiceNumber();
        setLedgerID("");
        setOwnerID("");
        setComent("");
        setPaymentOptions("");
        setTotal("");
        setOwnerField(null);
        setPayField(null);
        setSaving(false);
        setInitialFormSnapshot(createEmptySnapshot(defaultDateForFiscalYear));
      })
      .catch((error) => {
        setSaving(false);
        if (error.response && error.response.status === 409) {
          errorAlert(error.response.data);
        } else {
          errorAlert("Nuk u ruajt deponimi. Ju lutem provoni përsëri.");
        }
      });
  };

  const formatDateDdMmYyyy = (isoDate) => {
    if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
      return isoDate || "";
    }

    const [year, month, day] = isoDate.split("-");
    return `${day}.${month}.${year}`;
  };

  const formatCurrencyValue = (value) => {
    const numericValue = Number(value || 0);
    if (!Number.isFinite(numericValue)) {
      return "0.00";
    }

    return numericValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const selectedLedger = ledgers.find((item) => item.LedgerID === Number(ledgerID));
  const selectedPaymentAccount = accounts.find((item) => item.AccountID === Number(paymentOptions));
  const isPrintReady = Boolean(date && ledgerID && paymentOptions && total && ownerID && coment.trim());

  const sharedFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#f7fbf9",
      minHeight: "50px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d4e2db",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#4E7B67",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#4E7B67",
      borderWidth: "2px",
    },
    "& .MuiInputLabel-root": {
      color: "#476057",
    },
    "& .MuiInputBase-input": {
      fontSize: "14px",
      paddingTop: "13px",
      paddingBottom: "13px",
    },
  };

  const sharedMenuProps = {
    PaperProps: {
      sx: {
        mt: 0.5,
        border: "1px solid #dce9e2",
        borderRadius: "12px",
        boxShadow: "0 10px 24px rgba(31, 58, 54, 0.12)",
        maxHeight: 260,
        "& .MuiMenuItem-root": {
          fontSize: "14px",
          minHeight: 38,
        },
      },
    },
  };

  if (isInitializing) {
    return (
      <div className="RFM__page">
        <div className="RFM__screen" style={{ minHeight: "260px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "260px" }}>
            <CircularProgress size={28} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="RFM__page">
      <div className="RFM__screen print-hide">
        <div className="RFM__header">
          <div className="RFM__titleWrap">
            <h1 className="RFM__title">{isEditMode ? "Përditëso Deponimin" : "Deponim i Ri Kapitali"}</h1>
            <p className="RFM__subtitle">
              {isEditMode
                ? "Përditëso fushat kryesore dhe ruaj ndryshimet."
                : "Plotëso fushat kryesore dhe ruaj deponimin e kapitalit."}
            </p>
          </div>
          <div className="RFM__headerActions">
            <button type="button" className="RFM__btn RFM__btn--light" onClick={handlePrint} disabled={saving || !isPrintReady}>
              <IoPrintOutline />
              Printo Mandatin
            </button>
            <button type="submit" form="capital-deposit-form" className="RFM__btn RFM__btn--primary" disabled={saving || !hasUnsavedData}>
              {saving ? (
                <>
                  <CircularProgress style={{ color: "white", width: 18, height: 18 }} />
                  Duke ruajtur...
                </>
              ) : (
                <>{isEditMode ? "Përditëso" : "Ruaj & Ri"}</>
              )}
            </button>
            <button type="button" className="RFM__btn RFM__btn--light" onClick={handleClose}>
              <FaTimes />
              Mbyll
            </button>
          </div>
        </div>

        <div className="RFM__card">
          <div className="RFM__cardHeader">
            <div>
              <h2>{isEditMode ? "Përditësimi i Deponimit" : "Regjistrimi i Deponimit"}</h2>
              <p>
                {isEditMode
                  ? "Përditësoni të dhënat e kërkuara dhe ruani ndryshimet."
                  : "Plotësoni të dhënat e kërkuara për regjistrim të saktë dhe të plotë."}
              </p>
            </div>
            <div className="RFM__transferNo">CAD-{fiscalYear}-{invoiceNR || "..."}</div>
          </div>

          <form id="capital-deposit-form" className="RFM__form" onSubmit={handleSubmit} noValidate>
            <div className="RFM__grid RFM__grid--general">
              <div className="RFM__field RFM__field--invoice">
                <TextField label="Numri i Deponimit" value={`CAD-${fiscalYear}-${invoiceNR}`} disabled fullWidth />
              </div>

              <div className="RFM__field RFM__field--date">
                <DateInputDdMmYyyy
                  label="Data"
                  value={date}
                  minDate={minDate}
                  maxDate={maxDate}
                  onChange={(nextIsoDate) => {
                    setDate(nextIsoDate);
                    clearValidationError("date");
                    markDirty();
                  }}
                />
                {validationErrors.date ? <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 6 }}>{validationErrors.date}</div> : null}
              </div>

              <div className="RFM__field RFM__field--amount">
                <PriceField
                  label="Vlera"
                  name="total"
                  value={total}
                  onChange={(event) => {
                    setTotal(event.target.value ?? "");
                    clearValidationError("total");
                    markDirty();
                  }}
                  decimalScale={2}
                />
                {validationErrors.total ? <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 6 }}>{validationErrors.total}</div> : null}
              </div>
            </div>

            <div className="RFM__separator" />

            <div className="RFM__grid RFM__grid--accounts">
              <section className="RFM__panel RFM__panel--debtor">
                <h3>Kreditore (Burimi)</h3>

                <TextField
                  select
                  label="Llogaria kontabël Kreditore"
                  value={ledgerID}
                  onChange={(event) => {
                    setLedgerID(event.target.value);
                    fetchAccounts(event.target.value);
                    setPaymentOptions("");
                    setPayField(null);
                    clearValidationError("ledgerID");
                    clearValidationError("paymentOptions");
                    markDirty();
                  }}
                  fullWidth
                  size="small"
                  error={Boolean(validationErrors.ledgerID)}
                  helperText={validationErrors.ledgerID || ""}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ MenuProps: sharedMenuProps }}
                  sx={sharedFieldSx}
                >
                  {ledgers.map((ledger) => (
                    <MenuItem key={ledger.LedgerID} value={ledger.LedgerID}>
                      {ledger.LedgerNameEN}
                    </MenuItem>
                  ))}
                </TextField>

                <Autocomplete
                  fullWidth
                  {...defaultPropsPayAccount}
                  noOptionsText="Nuk u gjet asnjë rezultat"
                  onChange={handleAutocompleteChangePayAccount}
                  value={payField}
                  isOptionEqualToValue={(option, value) => option.AccountID === value?.AccountID}
                  ListboxProps={{ style: { maxHeight: 220, overflow: "auto", paddingTop: 2, paddingBottom: 2 } }}
                  renderOption={(props, option) => (
                    <li {...props} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #edf3ef", borderRadius: "8px", marginBottom: "2px", padding: "9px 10px", backgroundColor: "#ffffff" }}>
                      <div>
                        <div style={{ fontSize: 12, color: "#5f6f68", letterSpacing: "0.1px" }}>{option.IdentificationNumber || "Pa ID"}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#1f3a36" }}>{option.AccountName}</div>
                      </div>
                      <span style={{ color: "#7a9388", fontSize: 14, fontWeight: 700 }}>›</span>
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="Llogaria e Deponimit" size="small" error={Boolean(validationErrors.paymentOptions)} helperText={validationErrors.paymentOptions || ""} InputLabelProps={{ shrink: true }} sx={sharedFieldSx} />}
                />
              </section>

              <section className="RFM__panel RFM__panel--creditor">
                <h3>Debitore (Destinacioni)</h3>

                <TextField label="Llogaria kontabël Debitore" value="501-Kapitali pronarit" disabled fullWidth size="small" InputLabelProps={{ shrink: true }} sx={sharedFieldSx} />

                <div className="RFM__accountWithModal">
                  <div className="RFM__accountWithModalField">
                    <Autocomplete
                      fullWidth
                      options={owners}
                      value={ownerField}
                      onChange={(event, value) => {
                        if (value && value.AccountID) {
                          setOwnerID(value.AccountID);
                          setOwnerField(value);
                          clearValidationError("ownerID");
                          markDirty();
                        } else {
                          setOwnerID("");
                          setOwnerField(null);
                          markDirty();
                        }
                      }}
                      isOptionEqualToValue={(option, value) => option.AccountID === value?.AccountID}
                      getOptionLabel={(option) => `${option?.IdentificationNumber || ""} - ${option?.AccountName || ""}`}
                      ListboxProps={{ style: { maxHeight: 220, overflow: "auto", paddingTop: 2, paddingBottom: 2 } }}
                      renderOption={(props, option) => (
                        <li {...props} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #edf3ef", borderRadius: "8px", marginBottom: "2px", padding: "9px 10px", backgroundColor: "#ffffff" }}>
                          <div>
                            <div style={{ fontSize: 12, color: "#5f6f68", letterSpacing: "0.1px" }}>{option.IdentificationNumber || "Pa ID"}</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: "#1f3a36" }}>{option.AccountName}</div>
                          </div>
                          <span style={{ color: "#7a9388", fontSize: 14, fontWeight: 700 }}>›</span>
                        </li>
                      )}
                      renderInput={(params) => <TextField {...params} label="Pronari" size="small" error={Boolean(validationErrors.ownerID)} helperText={validationErrors.ownerID || ""} InputLabelProps={{ shrink: true }} sx={sharedFieldSx} />}
                    />
                  </div>
                  <IconButton size="small" onClick={() => setShowOwnerModal(true)} aria-label="Shto pronar" title="Shto pronar" className="RFM__inlineAddBtn">+</IconButton>
                </div>
              </section>
            </div>

            <div className="RFM__separator" />

            <div className="RFM__grid RFM__grid--general">
              <div className="RFM__field RFM__field--comment">
                <TextField
                  label="Komenti"
                  value={coment}
                  onChange={(event) => {
                    setComent(event.target.value);
                    clearValidationError("coment");
                    markDirty();
                  }}
                  multiline
                  minRows={3}
                  error={Boolean(validationErrors.coment)}
                  helperText={validationErrors.coment || ""}
                  placeholder="Shkruaj përshkrimin e deponimit..."
                  fullWidth
                />
              </div>
            </div>
          </form>
        </div>

        <OwnersModal show={showOwnerModal} onHide={handleOwnerModalClose} fetchData={fetchOwner} mode="insert" type="none" />
      </div>

      <div className="RFM__print print-only">
        <div className="RFM__printDoc">
          <div className="RFM__printHeader">
            <div className="RFM__printBrand">
              {companyDetails?.CompanyLogo ? (
                <div className="RFM__printLogoWrap">
                  <img src={ApiLink.PHOTO_URL + companyDetails.CompanyLogo} alt="Company logo" className="RFM__printLogo" />
                </div>
              ) : null}
              <h2>{companyDetails?.CompanyName || "Kompania"}</h2>
              <p>{companyDetails?.Address || "-"}</p>
              <div className="RFM__printCompanyMeta">
                <span>NUI: {companyDetails?.IdentificationNumber || "-"}</span>
                <span>Telefon: {companyDetails?.PhoneNumbers || "-"}</span>
              </div>
            </div>
            <div className="RFM__printTitleBox">
              <h1>MANDAT DEPONIMI</h1>
              <p>Dokument financiar</p>
            </div>
          </div>

          <div className="RFM__printMetaStrip">
            <div className="RFM__printMetaItem">
              <span>Nr. dokumentit</span>
              <strong className="RFM__mono">{savedDeposit?.DocumentNumber || `CAD-${fiscalYear}-${invoiceNR}`}</strong>
            </div>
            <div className="RFM__printMetaItem">
              <span>Data e dokumentit</span>
              <strong>{formatDateDdMmYyyy(savedDeposit?.Date || date)}</strong>
            </div>
            <div className="RFM__printMetaItem">
              <span>Viti fiskal</span>
              <strong>{fiscalYear}</strong>
            </div>
            <div className="RFM__printMetaItem">
              <span>Data e printimit</span>
              <strong>{formatDateDdMmYyyy(new Date().toISOString().slice(0, 10))}</strong>
            </div>
          </div>

          <div className="RFM__printSectionTitle">Palet e Transaksionit</div>
          <div className="RFM__printPartyGrid">
            <div className="RFM__printPartyCard">
              <h3>Kreditore (Burimi)</h3>
              <div className="RFM__printPartyRow">
                <span>Llogaria kontabël</span>
                <strong>{savedDeposit?.LedgerName || selectedLedger?.LedgerNameEN || "-"}</strong>
              </div>
              <div className="RFM__printPartyRow">
                <span>Llogaria e deponimit</span>
                <strong>{savedDeposit?.PaymentAccountName || selectedPaymentAccount?.AccountName || payField?.AccountName || "-"}</strong>
              </div>
            </div>

            <div className="RFM__printPartyCard">
              <h3>Debitore (Destinacioni)</h3>
              <div className="RFM__printPartyRow">
                <span>Pronari</span>
                <strong>{savedDeposit?.OwnerAccountName || ownerField?.AccountName || "-"}</strong>
              </div>
              <div className="RFM__printPartyRow">
                <span>Nr. identifikues</span>
                <strong>{savedDeposit?.OwnerIdentification || ownerField?.IdentificationNumber || "-"}</strong>
              </div>
            </div>
          </div>

          <div className="RFM__printSectionTitle">Detajet Financiare</div>
          <div className="RFM__printTable">
            <div className="RFM__printTableRow">
              <span>Vlera totale e deponimit</span>
              <strong>{formatCurrencyValue(savedDeposit?.Total || total)} EUR</strong>
            </div>
          </div>

          <div className="RFM__printSectionTitle">Pershkrimi</div>
          <div className="RFM__printCommentBox RFM__printCommentBox--doc">
            {savedDeposit?.Coment || coment || "-"}
          </div>

          <div className="RFM__printSignatureRow">
            <div className="RFM__printSignature">
              <span>Përpiloi</span>
              <div className="RFM__printSignatureLine" />
            </div>
            <div className="RFM__printSignature">
              <span>Pranoi</span>
              <div className="RFM__printSignatureLine" />
            </div>
          </div>

          <div className="RFM__printFooterNote">Dokument i gjeneruar elektronikisht dhe i vlefshëm për evidencë kontabël.</div>
          <div className="RFM__printCopyright">Copyright © {new Date().getFullYear()} Accountree. Të gjitha të drejtat e rezervuara.</div>
        </div>
      </div>

      {showConfirmClose && (
        <div className="RFM__modalOverlay">
          <div className="RFM__modal">
            <h3>Kujdes</h3>
            <p>Keni ndryshime të paruajtura. A dëshironi të mbyllet forma pa ruajtur?</p>
            <div className="RFM__modalActions">
              <button type="button" className="RFM__btn RFM__btn--light" onClick={() => setShowConfirmClose(false)}>
                Jo, qëndro
              </button>
              <button type="button" className="RFM__btn RFM__btn--danger" onClick={confirmClose}>
                Po, mbylle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CapitalDepositForm;
